import {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import API from "../../services/api";

import OrderSummary from "../../components/Checkout/OrderSummary";
import AddressSection from "../../components/Checkout/AddressSection";
import PaymentSection from "../../components/Checkout/PaymentSection";
import BillSummary from "../../components/Checkout/BillSummary";

import { useCart } from "../../context/CartContext";

import "../../components/Checkout/Checkout.css";


function Checkout() {

  const navigate =
    useNavigate();

  const location =
    useLocation();


  // =========================================================
  // ACCEPTED ORDER FROM CHAT
  // =========================================================

  const acceptedOrder =
    location.state?.acceptedOrder ||
    null;


  // =========================================================
  // CHAT CHECKOUT
  // =========================================================

  const isChatCheckout =
    Boolean(
      acceptedOrder
    );


  

  // =========================================================
  // CART CONTEXT
  // =========================================================

  const {
    clearCart,
  } = useCart();


  // =========================================================
  // STATE
  // =========================================================

  const [cart, setCart] =
    useState([]);


  const [addresses, setAddresses] =
    useState([]);


  const [selectedAddress, setSelectedAddress] =
    useState("");


  const [paymentMethod, setPaymentMethod] =
    useState("COD");


  const [coupon] =
    useState(null);


  const [loading, setLoading] =
    useState(true);


  const [placingOrder, setPlacingOrder] =
    useState(false);


  // =========================================================
  // IMPORTANT
  //
  // For chat/custom checkout:
  //
  // acceptedOrder = order information coming from messaging
  //
  // checkoutOrder = COMPLETE order fetched from backend
  //
  // We use checkoutOrder for pricing.
  // =========================================================

  const [checkoutOrder, setCheckoutOrder] =
  useState(
    acceptedOrder
  );


// =========================================================
// CATERING DETECTION
// =========================================================

const isCateringOrder =
  acceptedOrder?.providerType
    ?.toString()
    .toLowerCase() === "catering" ||

  acceptedOrder?.orderType
    ?.toString()
    .toLowerCase() === "catering" ||

  checkoutOrder?.providerType
    ?.toString()
    .toLowerCase() === "catering" ||

  checkoutOrder?.orderType
    ?.toString()
    .toLowerCase() === "catering" ||

  Array.isArray(
    acceptedOrder?.menuItems
  ) ||

  Array.isArray(
    checkoutOrder?.menuItems
  ) ||

  Array.isArray(
    acceptedOrder?.cateringDetails?.menuItems
  ) ||

  Array.isArray(
    checkoutOrder?.cateringDetails?.menuItems
  ) ||

  Boolean(
    acceptedOrder?.cateringDetails
  ) ||

  Boolean(
    checkoutOrder?.cateringDetails
  );


const billSummaryRef =
  useRef(null);


  const [showStickyBar, setShowStickyBar] =
    useState(false);


  // =========================================================
  // GET CUSTOM ORDER ID
  // =========================================================

  const customOrderId =
    acceptedOrder?.orderId ||

    acceptedOrder?._id ||

    acceptedOrder?.customOrderId ||

    null;


  // =========================================================
  // GET CATERING GUESTS
  // =========================================================

  const cateringGuests =
    Number(

      checkoutOrder?.guests ??

      checkoutOrder?.guestCount ??

      checkoutOrder?.cateringDetails?.guests ??

      checkoutOrder?.cateringDetails?.guestCount ??

      acceptedOrder?.guests ??

      acceptedOrder?.guestCount ??

      acceptedOrder?.cateringDetails?.guests ??

      acceptedOrder?.cateringDetails?.guestCount ??

      0

    ) || 0;


  // =========================================================
  // GET FUNCTION NAME
  // =========================================================

  const cateringFunctionName =

    checkoutOrder?.functionName ||

    checkoutOrder?.cateringDetails?.functionName ||

    checkoutOrder?.customOrderDetails?.functionName ||

    acceptedOrder?.functionName ||

    acceptedOrder?.cateringDetails?.functionName ||

    acceptedOrder?.customOrderDetails?.functionName ||

    "";


  // =========================================================
  // GET FUNCTION DATE
  // =========================================================

  const cateringFunctionDate =

    checkoutOrder?.functionDate ||

    checkoutOrder?.cateringDetails?.functionDate ||

    checkoutOrder?.customOrderDetails?.functionDate ||

    acceptedOrder?.functionDate ||

    acceptedOrder?.cateringDetails?.functionDate ||

    acceptedOrder?.customOrderDetails?.functionDate ||

    "";


  // =========================================================
  // GET ITEMS FROM ORDER
  // =========================================================
  //
  // For actual checkout we prefer:
  //
  // checkoutOrder.items
  //
  // because those items contain the provider pricing.
  //
  // =========================================================

  const getOrderItems =
    (order) => {

      if (!order) {

        return [];

      }


      const candidates = [

        // Complete backend order
        order.items,

        // Direct menu items
        order.menuItems,

        // Catering
        order.cateringDetails?.menuItems,

        // Custom order
        order.customOrderDetails?.items,

        // Custom menu items
        order.customOrderDetails?.menuItems,

      ];


      const found =
        candidates.find(
          candidate =>
            Array.isArray(candidate) &&
            candidate.length > 0
        );


      return Array.isArray(found)
        ? found
        : [];

    };


  // =========================================================
  // GET ACCEPTED ITEMS
  // =========================================================

  const getAcceptedItems =
    () => {

      // Prefer complete backend order
      if (
        checkoutOrder?.items &&
        Array.isArray(
          checkoutOrder.items
        ) &&
        checkoutOrder.items.length > 0
      ) {

        return checkoutOrder.items;

      }


      // Fallback to original accepted order
      return getOrderItems(
        acceptedOrder
      );

    };


  // =========================================================
  // DEBUG CHAT CHECKOUT
  // =========================================================

  useEffect(() => {

    const acceptedItems =
      getAcceptedItems();


    console.log(
      "========================================"
    );

    console.log(
      "🟢 CHECKOUT PAGE"
    );

    console.log(
      "Location State:",
      location.state
    );

    console.log(
      "Accepted Order:",
      acceptedOrder
    );

    console.log(
      "Complete Checkout Order:",
      checkoutOrder
    );

    console.log(
      "Custom Order ID:",
      customOrderId
    );

    console.log(
      "Is Chat Checkout:",
      isChatCheckout
    );

    console.log(
      "Is Catering:",
      isCateringOrder
    );

    console.log(
      "Catering Guests:",
      cateringGuests
    );

    console.log(
      "Catering Function:",
      cateringFunctionName
    );

    console.log(
      "Catering Date:",
      cateringFunctionDate
    );

    console.log(
      "Accepted Items:",
      acceptedItems
    );

    console.log(
      "Accepted Item Count:",
      acceptedItems.length
    );

    console.log(
      "Backend Subtotal:",
      checkoutOrder?.subTotal
    );

    console.log(
      "Backend Grand Total:",
      checkoutOrder?.grandTotal
    );

    console.log(
      "========================================"
    );

  }, [
    location.state,
    acceptedOrder,
    checkoutOrder,
    customOrderId,
    isChatCheckout,
    isCateringOrder,
    cateringGuests,
    cateringFunctionName,
    cateringFunctionDate,
  ]);


  // =========================================================
  // FETCH NORMAL CART
  // =========================================================

  const fetchCart =
    async () => {

      try {

        const response =
          await API.get(
            "/cart"
          );


        const serverCart =
          response.data?.cart ||
          [];


        console.log(
          "🛒 Normal Cart:",
          serverCart
        );


        setCart(
          serverCart
        );


        return serverCart;

      } catch (error) {

        console.error(
          "❌ Checkout Cart Error:",
          error.response?.data ||
          error
        );


        setCart([]);


        return [];

      }

    };


  // =========================================================
  // FETCH COMPLETE CUSTOM ORDER
  // =========================================================
  //
  // IMPORTANT:
  //
  // Messaging acceptedOrder can contain only:
  //
  // itemName
  // _id
  //
  // It may NOT contain provider price.
  //
  // Therefore checkout fetches the complete order.
  //
  // =========================================================

  const fetchCompleteCustomOrder =
    async () => {

      if (
        !customOrderId
      ) {

        console.error(
          "❌ Custom order ID missing."
        );


        return acceptedOrder;

      }


      try {

        console.log(
          "🔄 Fetching complete custom order:",
          customOrderId
        );


       const response =
    await API.get(
        `/custom-orders/customer/${customOrderId}`
    );

        const fullOrder =
          response.data?.order;


        if (!fullOrder) {

          console.error(
            "❌ Complete custom order was not returned."
          );


          return acceptedOrder;

        }


        console.log(
          "========================================"
        );

        console.log(
          "✅ COMPLETE CUSTOM ORDER FROM BACKEND"
        );

        console.log(
          JSON.stringify(
            fullOrder,
            null,
            2
          )
        );

        console.log(
          "Backend Items:",
          fullOrder.items
        );

        console.log(
          "Backend Subtotal:",
          fullOrder.subTotal
        );

        console.log(
          "Backend Grand Total:",
          fullOrder.grandTotal
        );

        console.log(
          "========================================"
        );


        // Store complete order
        setCheckoutOrder(
          fullOrder
        );


        return fullOrder;

      } catch (error) {

        console.error(
          "❌ Failed to fetch complete custom order:",
          error.response?.data ||
          error
        );


        // Fallback
        return acceptedOrder;

      }

    };


  // =========================================================
  // PREPARE ACCEPTED CHAT ORDER
  // =========================================================

  const prepareAcceptedOrder =
    async () => {

      console.log(
        "========================================"
      );

      console.log(
        "🍽️ PREPARING ACCEPTED CHAT ORDER"
      );

      console.log(
        "Accepted Order:",
        acceptedOrder
      );


      // =====================================================
      // FETCH COMPLETE BACKEND ORDER
      // =====================================================

      const fullOrder =
        await fetchCompleteCustomOrder();


      // =====================================================
      // GET ACTUAL BACKEND ITEMS
      // =====================================================

      const acceptedItems =
        getOrderItems(
          fullOrder
        );


      console.log(
        "🔥🔥🔥 RAW BACKEND CUSTOM ORDER ITEMS 🔥🔥🔥"
      );

      console.log(
        JSON.stringify(
          acceptedItems,
          null,
          2
        )
      );


      console.log(
        "🔥🔥🔥 FULL BACKEND ORDER 🔥🔥🔥"
      );

      console.log(
        JSON.stringify(
          fullOrder,
          null,
          2
        )
      );


      console.log(
        "Accepted Items:",
        acceptedItems
      );


      console.log(
        "Accepted Item Count:",
        acceptedItems.length
      );


      console.log(
        "========================================"
      );


      // =====================================================
      // NO ITEMS
      // =====================================================

      if (
        acceptedItems.length === 0
      ) {

        console.error(
          "❌ Accepted custom order contains no items."
        );


        setCart([]);


        return false;

      }


      // =====================================================
      // FORMAT ITEMS
      // =====================================================

      const formattedItems =
        acceptedItems
          .map(
            (
              item,
              index
            ) => {

              if (!item) {

                return null;

              }


              // ===========================================
              // FOOD ID
              // ===========================================

              let foodId =
                null;


              if (
                typeof item.foodId ===
                  "object" &&
                item.foodId !== null
              ) {

                foodId =
                  item.foodId._id ||
                  item.foodId.id ||
                  null;

              } else {

                foodId =
                  item.foodId ||
                  item._id ||
                  null;

              }


              // ===========================================
              // CATERING ITEMS MAY NOT HAVE FOOD ID
              // ===========================================

              if (!foodId) {

                foodId =
                  `catering-item-${index}`;

              }


              // ===========================================
              // FOOD NAME
              // ===========================================

              const foodName =

                item.foodName ||

                item.itemName ||

                item.menuItemName ||

                item.name ||

                item.foodId?.foodName ||

                item.foodId?.name ||

                "Food Item";


              // ===========================================
              // PRICE
              // ===========================================
              //
              // IMPORTANT:
              //
              // For catering:
              //
              // item.price is already the provider's
              // price for this menu item.
              //
              // DO NOT multiply by guests here.
              //
              // ===========================================

             const priceCandidates = [

  // ==========================================
  // CATERING FINAL ITEM TOTAL
  // ==========================================
  item.total,

  // ==========================================
  // DIRECT PRICE
  // ==========================================
  item.price,

  // ==========================================
  // OTHER POSSIBLE PRICE FIELDS
  // ==========================================
  item.totalPrice,
  item.quotedPrice,
  item.finalPrice,
  item.menuPrice,
  item.offerPrice,

  item.foodId?.price,
  item.foodId?.offerPrice,

];


const price =
  priceCandidates
    .map(Number)
    .find(
      value =>
        Number.isFinite(value) &&
        value > 0
    ) || 0;

              console.log(
                "🍽️ CHECKOUT ITEM PRICE:",
                {
                  name:
                    foodName,

                  itemId:
                    item._id,

                  price:
                    item.price,

                  total:
                    item.total,

                  menuPrice:
                    item.menuPrice,

                  quotedPrice:
                    item.quotedPrice,

                  finalPrice:
                    item.finalPrice,

                  

                  calculatedPrice:
                    price,

                }
              );


              // ===========================================
              // IMAGE
              // ===========================================

              const image =

                item.image ||

                item.foodImage ||

                item.menuImage ||

                item.foodId?.image ||

                item.foodId?.foodImage ||

                "";


              // ===========================================
              // OFFER PRICE
              // ===========================================

              const offerPrice =
                Number(
                  item.offerPrice ??
                  item.menuPrice ??
                  item.price ??
                  item.foodId?.offerPrice ??
                  0
                ) || 0;


              // ===========================================
              // QUANTITY
              // ===========================================
              //
              // Catering:
              //
              // quantity = 1
              //
              // The price is NOT multiplied by guest count.
              //
              // ===========================================

              const quantity =

                isCateringOrder

                  ? 1

                  : (
                    Number(
                      item.quantity
                    ) || 1
                  );


              // ===========================================
              // UNIT
              // ===========================================

              const unit =

                item.unit ||

                item.foodId?.unit ||

                (
                  isCateringOrder
                    ? "menu item"
                    : "item"
                );


              // ===========================================
              // FOOD OBJECT
              // ===========================================

              const foodObject = {

                _id:
                  foodId ||
                  `chat-food-${index}`,

                name:
                  foodName,

                foodName:
                  foodName,

                price:
                  price,

                offerPrice:
                  offerPrice,

                image:
                  image,

                unit:
                  unit,

                providerId:
                  item.providerId ||

                  item.foodId?.providerId ||

                  fullOrder?.providerId ||

                  null,

              };


              // ===========================================
              // CHECKOUT ITEM
              // ===========================================

              return {

                _id:

                  item._id ||

                  foodId ||

                  `chat-item-${index}`,

                foodId:

                  foodObject,

                // IMPORTANT
                // Put actual backend price at top level.
                price:

                  price,

                quantity:

                  quantity,

                unit:

                  unit,

                providerId:

                  item.providerId ||

                  item.foodId?.providerId ||

                  fullOrder?.providerId ||

                  acceptedOrder?.providerId ||

                  null,

                isAcceptedOrder:

                  true,

                isCateringOrder:

                  isCateringOrder,

                cateringGuests:

                  isCateringOrder
                    ? cateringGuests
                    : 0,

                // Keep complete backend item
                originalItem:

                  item,

              };

            }
          )
          .filter(Boolean);


      // =====================================================
      // DEBUG
      // =====================================================

      console.log(
        "========================================"
      );

      console.log(
        "✅ FORMATTED CHAT ITEMS"
      );

      console.log(
        formattedItems
      );

      console.log(
        "Formatted Item Count:",
        formattedItems.length
      );

      console.log(
        "========================================"
      );


      // =====================================================
      // SET CART
      // =====================================================

      setCart(
        formattedItems
      );


      return (
        formattedItems.length > 0
      );

    };


  // =========================================================
  // LOAD ADDRESSES
  // =========================================================

  const fetchAddresses =
    async () => {

      try {

        const response =
          await API.get(
            "/address"
          );


        const loadedAddresses =
          response.data?.addresses ||
          [];


        console.log(
          "📍 Checkout Addresses:",
          loadedAddresses
        );


        setAddresses(
          loadedAddresses
        );


        // =====================================================
        // AUTO SELECT FIRST ADDRESS
        // =====================================================

        if (
          loadedAddresses.length > 0
        ) {

          const firstAddress =
            loadedAddresses[0];


          const addressId =
            firstAddress._id ||
            firstAddress.id;


          if (addressId) {

            setSelectedAddress(
              previous =>
                previous ||
                addressId
            );

          }

        }

      } catch (error) {

        console.error(
          "❌ Checkout Address Error:",
          error.response?.data ||
          error
        );


        setAddresses([]);

      }

    };


  // =========================================================
  // REMOVE ITEM
  // =========================================================

  const removeItem =
    async (
      cartId
    ) => {

      // =====================================================
      // CHAT CHECKOUT
      // =====================================================

      if (
        isChatCheckout
      ) {

        console.log(
          "🗑️ Removing chat checkout item:",
          cartId
        );


        setCart(
          previous =>
            previous.filter(
              item =>
                item._id !== cartId
            )
        );


        return;

      }


      // =====================================================
      // NORMAL CART
      // =====================================================

      try {

        await API.delete(
          `/cart/remove/${cartId}`
        );


        await fetchCart();

      } catch (error) {

        console.error(
          "❌ Remove Cart Item Error:",
          error.response?.data ||
          error
        );

      }

    };


  // =========================================================
  // PLACE ORDER
  // =========================================================

  const placeOrder =
    async () => {

      console.log(
        "========================================"
      );

      console.log(
        "📦 PLACE ORDER"
      );

      console.log(
        "Selected Address:",
        selectedAddress
      );

      console.log(
        "Payment:",
        paymentMethod
      );

      console.log(
        "Cart:",
        cart
      );

      console.log(
        "Is Chat Checkout:",
        isChatCheckout
      );

      console.log(
        "Is Catering:",
        isCateringOrder
      );

      console.log(
        "Accepted Order:",
        acceptedOrder
      );

      console.log(
        "Complete Checkout Order:",
        checkoutOrder
      );

      console.log(
        "========================================"
      );


      // =====================================================
      // ADDRESS
      // =====================================================

      if (!selectedAddress) {

        alert(
          "Please select a delivery address."
        );

        return;

      }


      // =====================================================
      // CART
      // =====================================================

      if (
        !cart ||
        cart.length === 0
      ) {

        alert(
          "Your order is empty."
        );

        return;

      }


      try {

        setPlacingOrder(
          true
        );


        // ===================================================
        // CHAT / CUSTOM ORDER
        // ===================================================

        if (
          isChatCheckout
        ) {

          const customOrderId =

            checkoutOrder?.orderId ||

            checkoutOrder?._id ||

            checkoutOrder?.customOrderId ||

            acceptedOrder?.orderId ||

            acceptedOrder?._id ||

            acceptedOrder?.customOrderId;


          if (!customOrderId) {

            throw new Error(
              "Custom order ID is missing."
            );

          }


          // ===============================================
          // PAYMENT METHOD
          // ===============================================

          const backendPaymentMethod =

            paymentMethod === "COD"

              ? "Cash On Delivery"

              : paymentMethod;


          // ===============================================
          // FINAL CUSTOM ORDER PAYLOAD
          // ===============================================

          const checkoutPayload = {

            deliveryAddress:
              selectedAddress,

            paymentMethod:
              backendPaymentMethod,

          };


          console.log(
            "========================================"
          );

          console.log(
            "🟢 CUSTOM ORDER CHECKOUT"
          );

          console.log(
            "Custom Order ID:",
            customOrderId
          );

          console.log(
            "Provider Type:",
            checkoutOrder?.providerType
          );

          console.log(
            "Order Type:",
            checkoutOrder?.orderType
          );

          console.log(
            "Catering:",
            isCateringOrder
          );

          console.log(
            "Function:",
            cateringFunctionName
          );

          console.log(
            "Function Date:",
            cateringFunctionDate
          );

          console.log(
            "Guests:",
            cateringGuests
          );

          console.log(
            "Menu Items:",
            getAcceptedItems()
          );

          console.log(
            "Backend Subtotal:",
            checkoutOrder?.subTotal
          );

          console.log(
            "Backend Grand Total:",
            checkoutOrder?.grandTotal
          );

          console.log(
            "Checkout Payload:",
            checkoutPayload
          );

          console.log(
            "========================================"
          );


          // ===============================================
          // BACKEND CHECKOUT
          // ===============================================

          const response =
            await API.put(

              `/custom-orders/${customOrderId}/checkout`,

              checkoutPayload

            );


          console.log(
            "✅ Custom Checkout Response:",
            response.data
          );


          if (
            !response.data?.success
          ) {

            throw new Error(

              response.data?.message ||

              "Failed to checkout custom order."

            );

          }


          // ===============================================
          // SUCCESS
          // ===============================================

          alert(
            isCateringOrder

              ? "🎉 Catering Order Placed Successfully"

              : "🎉 Custom Order Placed Successfully"
          );


          navigate(
            "/customer/order-success",
            {

              replace:
                true,

              state: {

                fromChatCheckout:
                  true,

                isCateringOrder:
                  isCateringOrder,

                order:
                  response.data.order,

              },

            }
          );


          return;

        }


        // ===================================================
        // NORMAL FOOD ORDER
        // ===================================================

        for (
          const item of cart
        ) {

          const foodId =

            item.foodId?._id ||

            item.foodId;


          const quantity =
            Number(
              item.quantity
            ) || 1;


          if (!foodId) {

            console.error(
              "❌ Food ID missing:",
              item
            );


            throw new Error(
              "Food information is missing for one of your items."
            );

          }


          const orderData = {

            foodId:
              foodId,

            quantity:
              quantity,

            deliveryAddress:
              selectedAddress,

            paymentMethod:
              paymentMethod,

          };


          console.log(
            "📤 Sending Normal Order:",
            orderData
          );


          const response =
            await API.post(
              "/orders",
              orderData
            );


          console.log(
            "✅ Order API Response:",
            response.data
          );

        }


        // ===================================================
        // CLEAR NORMAL CART
        // ===================================================

        await clearCart();


        // ===================================================
        // SUCCESS
        // ===================================================

        alert(
          "🎉 Order Placed Successfully"
        );


        navigate(
          "/customer/order-success",
          {

            replace:
              true,

            state: {

              fromChatCheckout:
                false,

            },

          }
        );

      } catch (error) {

        console.error(
          "❌ Place Order Error:",
          error.response?.data ||
          error
        );


        alert(

          error.response?.data?.message ||

          error.message ||

          "Failed to place order."

        );

      } finally {

        setPlacingOrder(
          false
        );

      }

    };


  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {

    let cancelled =
      false;


    const loadCheckout =
      async () => {

        setLoading(
          true
        );


        try {

          // =================================================
          // CHAT CHECKOUT
          // =================================================

          if (
            isChatCheckout
          ) {

            console.log(
              "🟢 Loading CHAT CHECKOUT"
            );


            const formattedItems =
              await prepareAcceptedOrder();


            if (
              formattedItems === false
            ) {

              console.error(
                "❌ Accepted custom order contains no items."
              );


              setCart([]);

            }

          } else {

            // ===============================================
            // NORMAL CART
            // ===============================================

            console.log(
              "🛒 Loading NORMAL CART CHECKOUT"
            );


            await fetchCart();

          }


          // =================================================
          // ADDRESS
          // =================================================

          await fetchAddresses();

        } catch (error) {

          console.error(
            "❌ Checkout Load Error:",
            error
          );

        } finally {

          if (!cancelled) {

            setLoading(
              false
            );

          }

        }

      };


    loadCheckout();


    return () => {

      cancelled =
        true;

    };


    // The acceptedOrder is intentionally consumed
    // through isChatCheckout and location state.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [
    isChatCheckout,
  ]);


  // =========================================================
  // STICKY BAR
  // =========================================================

  useEffect(() => {

    const handleScroll =
      () => {

        if (
          !billSummaryRef.current
        ) {

          return;

        }


        const rect =
          billSummaryRef.current
            .getBoundingClientRect();


        setShowStickyBar(

          rect.top <=
          window.innerHeight - 150

        );

      };


    window.addEventListener(
      "scroll",
      handleScroll
    );


    handleScroll();


    return () => {

      window.removeEventListener(
        "scroll",
        handleScroll
      );

    };

  }, []);


  // =========================================================
  // CHAT ITEMS
  // =========================================================

  const chatItems =
    isChatCheckout
      ? getAcceptedItems()
      : [];


  // =========================================================
  // CALCULATED CHAT SUBTOTAL
  // =========================================================
  //
  // This is only a FALLBACK.
  //
  // Backend checkoutOrder.subTotal is preferred.
  //
  // =========================================================

  const calculatedChatSubtotal =
  chatItems.reduce(
    (
      sum,
      item
    ) => {

      if (!item) {
        return sum;
      }

      const priceCandidates = [

  // Catering item final total
  item?.total,

  // Direct price
  item?.price,

  // Other possible backend fields
  item?.totalPrice,
  item?.quotedPrice,
  item?.finalPrice,
  item?.menuPrice,
  item?.offerPrice,

  item?.foodId?.price,
  item?.foodId?.offerPrice,

];


const price =
  priceCandidates
    .map(Number)
    .find(
      value =>
        Number.isFinite(value) &&
        value > 0
    ) || 0;


      /*
       * =====================================================
       * CATERING
       * =====================================================
       *
       * Provider price is already the price for the
       * complete catering requirement.
       *
       * Example:
       *
       * Guests = 100
       * Biryani price = ₹12,500
       *
       * Subtotal = ₹12,500
       *
       * NOT:
       *
       * ₹12,500 × 100
       *
       * =====================================================
       */

      const quantity =
        isCateringOrder
          ? 1
          : (
              Number(
                item.quantity
              ) || 1
            );


      return (
        sum +
        (
          price *
          quantity
        )
      );

    },
    0
  );

  // =========================================================
  // SUBTOTAL
  // =========================================================

 const subtotal =

  isChatCheckout

    ? (

        isCateringOrder

          ? calculatedChatSubtotal

          : (
              Number(
                checkoutOrder?.subTotal ??
                checkoutOrder?.subtotal
              ) ||
              calculatedChatSubtotal
            )

      )

    : cart.reduce(
        (
          sum,
          item
        ) => {

          const offerPrice =
            Number(
              item.foodId?.offerPrice
            ) || 0;


          const normalPrice =
            Number(
              item.foodId?.price
            ) || 0;


          const price =
            offerPrice > 0
              ? offerPrice
              : normalPrice;


          const quantity =
            Number(
              item.quantity
            ) || 1;


          return (
            sum +
            price * quantity
          );

        },
        0
      );


  // =========================================================
  // DELIVERY FEE
  // =========================================================

  const deliveryFee =

    isChatCheckout

      ? (
          Number(
            checkoutOrder?.deliveryCharge
          ) || 0
        )

      : (
          subtotal >= 499
            ? 0
            : 40
        );


  // =========================================================
  // PLATFORM FEE
  // =========================================================
  //
  // IMPORTANT:
  //
  // For custom/catering orders use backend platformFee.
  //
  // DO NOT create another 5% fee here.
  //
  // =========================================================

  const platformFee =

    isChatCheckout

      ? (
          Number(
            checkoutOrder?.platformFee
          ) || 0
        )

      : 5;


  // =========================================================
  // PACKING FEE
  // =========================================================

  const packingFee =

    isChatCheckout

      ? (
          Number(
            checkoutOrder?.packingCharge
          ) || 0
        )

      : 15;


  // =========================================================
  // GST
  // =========================================================

  const gst =

    isChatCheckout

      ? (
          Number(
            checkoutOrder?.gst
          ) || 0
        )

      : Math.round(
          subtotal *
          0.05
        );


  // =========================================================
  // DISCOUNT
  // =========================================================

  const discount =

    isChatCheckout

      ? (
          Number(
            checkoutOrder?.discount
          ) || 0
        )

      : 0;


  // =========================================================
  // CALCULATED GRAND TOTAL
  // =========================================================

  const calculatedGrandTotal =

    subtotal +

    gst +

    packingFee +

    deliveryFee +

    platformFee -

    discount;


  // =========================================================
  // GRAND TOTAL
  // =========================================================
  //
  // For chat/custom order:
  //
  // Backend grandTotal is the source of truth.
  //
  // If missing, calculate it as fallback.
  //
  // =========================================================

  const grandTotal =

  isChatCheckout

    ? (

        isCateringOrder

          ? calculatedGrandTotal

          : (
              Number(
                checkoutOrder?.grandTotal
              ) ||
              calculatedGrandTotal
            )

      )

    : calculatedGrandTotal;

  // =========================================================
  // LOADING
  // =========================================================

  if (
    loading
  ) {

    return (

      <div className="checkout-page">

        <div className="checkout-loading">

          <h2>
            Loading Checkout...
          </h2>

        </div>

      </div>

    );

  }


  // =========================================================
  // EMPTY
  // =========================================================

  if (
    !cart ||
    cart.length === 0
  ) {
console.log("========== CHECKOUT RENDER ==========");

console.log("cart:", cart);
console.log("cart length:", cart.length);

console.log("isChatCheckout:", isChatCheckout);
console.log("isCateringOrder:", isCateringOrder);

console.log("checkoutOrder:", checkoutOrder);

console.log("subtotal:", subtotal);
console.log("deliveryFee:", deliveryFee);
console.log("platformFee:", platformFee);
console.log("packingFee:", packingFee);
console.log("gst:", gst);
console.log("grandTotal:", grandTotal);

console.log("====================================");
    return (

      <div className="checkout-page fade-in">

        <div className="checkout-empty">

          <h2>
            Your order is empty
          </h2>


          <p>

            {isCateringOrder

              ? "No catering menu items are available for checkout."

              : "There are no items available for checkout."

            }

          </p>


          <button
            type="button"
            onClick={() =>
              navigate("/home")
            }
          >
            Continue Shopping
          </button>

        </div>

      </div>

    );

  }


  // =========================================================
  // MAIN UI
  // =========================================================

  return (

    <div className="checkout-page fade-in">

      {/* =====================================================
          HEADING
      ===================================================== */}

      <div className="checkout-heading">

        <span className="checkout-eyebrow">

          {isChatCheckout

            ? (
                isCateringOrder
                  ? "HomeFoods Catering"
                  : "HomeFoods Order"
              )

            : "HomeFoods"

          }

        </span>


        <h1 className="checkout-title">

          Checkout

        </h1>


        {isChatCheckout && (

          <p className="checkout-chat-info">

            {isCateringOrder

              ? "Your catering request has been priced. Review the details and complete checkout."

              : "Your provider has accepted your order. Complete checkout to place the order."

            }

          </p>

        )}

      </div>


      {/* =====================================================
          CATERING SUMMARY INFORMATION
      ===================================================== */}

      {isChatCheckout &&
        isCateringOrder && (

          <div
            className="checkout-catering-info"
          >

            {cateringFunctionName && (

              <div>

                <span>
                  Function
                </span>


                <strong>
                  {cateringFunctionName}
                </strong>

              </div>

            )}


            {cateringFunctionDate && (

              <div>

                <span>
                  Date
                </span>


                <strong>

                  {new Date(
                    cateringFunctionDate
                  ).toLocaleDateString(
                    "en-IN",
                    {
                      day:
                        "2-digit",

                      month:
                        "short",

                      year:
                        "numeric",

                    }
                  )}

                </strong>

              </div>

            )}


            <div>

              <span>
                Guests
              </span>


              <strong>
                {cateringGuests}
              </strong>

            </div>

          </div>

        )}


      {/* =====================================================
          ORDER SUMMARY
      ===================================================== */}

      <OrderSummary

        cart={
          cart
        }

        removeItem={
          removeItem
        }

        isCateringOrder={
          isCateringOrder
        }

        cateringGuests={
          cateringGuests
        }

      />


      {/* =====================================================
          ADDRESS
      ===================================================== */}

      <AddressSection

        addresses={
          addresses
        }

        selectedAddress={
          selectedAddress
        }

        setSelectedAddress={
          setSelectedAddress
        }

        refreshAddresses={
          fetchAddresses
        }

      />


      {/* =====================================================
          PAYMENT
      ===================================================== */}

      <PaymentSection

        paymentMethod={
          paymentMethod
        }

        setPaymentMethod={
          setPaymentMethod
        }

      />


      {/* =====================================================
          BILL SUMMARY
      ===================================================== */}

      <div
        ref={
          billSummaryRef
        }
      >

        <BillSummary

          subtotal={
            subtotal
          }

          deliveryFee={
            deliveryFee
          }

          platformFee={
            platformFee
          }

          packingFee={
            packingFee
          }

          gst={
            gst
          }

          grandTotal={
            grandTotal
          }

          coupon={
            coupon
          }

        />

      </div>


      {/* =====================================================
          STICKY PLACE ORDER
      ===================================================== */}

      {showStickyBar && (

        <div className="place-order-bar">

          <div className="place-order-container">

            <div className="order-total">

              <small>
                Grand Total
              </small>


              <h2>

                ₹
                {Number(
                  grandTotal
                ).toFixed(2)}

              </h2>


              <div className="order-meta">

                <span>
                  🚚 30–45 mins
                </span>


                <span className="order-dot">
                </span>


                <span>

                  {paymentMethod === "COD"

                    ? "Cash On Delivery"

                    : paymentMethod

                  }

                </span>

              </div>

            </div>


            <button
              type="button"
              className="place-order-btn"
              onClick={
                placeOrder
              }
              disabled={

                placingOrder ||

                !selectedAddress ||

                cart.length === 0

              }
            >

              {placingOrder

                ? "Placing Order..."

                : "Place Order →"

              }

            </button>

          </div>

        </div>

      )}

    </div>

  );

}


export default Checkout;